import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import {
  DataContextBuilder,
  DataViewItemDropEvent,
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderDataViewDndDirective,
  ElderFormFieldDenseDirective,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneSubtitleComponent,
  ElderPlugParentFormDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderValidationErrorDirective,
  IDataContext,
  LocalListDataSource,
  Sort,
  SortUtil,
  UuidUtil,
} from '@elderbyte/ngx-starter'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { PackageTypeDetailController } from '../package-type-detail-controller.service'
import { Observable } from 'rxjs'
import { PackageContentLabelSpec, PackageType } from '../../package-type'
import { AsyncPipe } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { Logger, LoggerFactory } from '@elderbyte/ts-logger'
import { moveItemInArray } from '@angular/cdk/drag-drop'
import { MatCheckbox } from '@angular/material/checkbox'

@Component({
  selector: 'app-package-type-labeling',
  imports: [
    ElderDataToolbarComponent,
    ElderTableComponent,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    ElderTranslatePipe,
    ElderTableToolbarDirective,
    MatHeaderCellDef,
    AsyncPipe,
    ElderFormFieldDenseDirective,
    ElderValidationErrorDirective,
    FormsModule,
    MatError,
    MatFormField,
    MatInput,
    ElderDataViewDndDirective,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneSubtitleComponent,
    ElderPaneContentComponent,
    MatCheckbox,
    MatLabel,
    ElderPlugParentFormDirective,
  ],
  templateUrl: './package-type-labeling.component.html',
  styleUrl: './package-type-labeling.component.scss',
})
export class PackageTypeLabelingComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log: Logger = LoggerFactory.getLogger(this.constructor.name)

  private readonly controller = inject(PackageTypeDetailController)

  private readonly _specSourceSort = [new Sort('outputOrder', 'asc')]
  private readonly _specSource: LocalListDataSource<PackageContentLabelSpec> =
    LocalListDataSource.empty('id')

  public readonly data: IDataContext<PackageContentLabelSpec>

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<PackageContentLabelSpec>()
      .autoStartAsap()
      .reloadOnLocalChanges()
      .build(this._specSource)

    this.controller.packageType$
      .pipe(takeUntilDestroyed())
      .subscribe((type) => this.initDataSource(type))
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get packageType$(): Observable<PackageType> {
    return this.controller.packageType$
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onAddContentLabelSpec(): void {
    const outputOrderIndex = this.nextSpecOutputOrderIndex()
    const spec = this.buildNewSpec(outputOrderIndex)

    this._specSource.save(spec, outputOrderIndex)
    this.trackOutputOrderOnModel()
    this.writeStateToModel()
  }

  public removeContentLabelSpec(specs: PackageContentLabelSpec[]): void {
    this._specSource.deleteAll(specs)
    this.trackOutputOrderOnModel()
    this.writeStateToModel()
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private initDataSource(type: PackageType): void {
    const specs = [...type.labelingSpec.contentLabelSpecs]
    this._specSource.data = this.ensureOrder(specs)
  }

  private ensureOrder(specs: PackageContentLabelSpec[]): PackageContentLabelSpec[] {
    return SortUtil.sortData(specs, this._specSourceSort)
  }

  private writeStateToModel(): void {
    const state = this._specSource.data
    this.controller.setContentLabelSpecs(state)
  }

  private nextSpecOutputOrderIndex(): number {
    return this._specSource.data?.length || 0
  }

  private buildNewSpec(outputOrder: number): PackageContentLabelSpec {
    return new PackageContentLabelSpec(UuidUtil.strongUuid4(), null, null, outputOrder)
  }

  public onItemDropped(event: DataViewItemDropEvent<PackageContentLabelSpec>): void {
    //const specList = event.fromContext.snapshot.data;
    const specList = this._specSource.data

    // first apply the order change on the data structure
    moveItemInArray(specList, event.fromIndex, event.toIndex)

    // then track the new order on the designated order property
    this.trackOutputOrderOnModel()

    // refresh data view to render changes
    this.data.refresh()
  }

  private trackOutputOrderOnModel(): void {
    const specs = this._specSource.data
    specs.forEach((item, index) => {
      item.outputOrder = index
    })
  }
}
